import { memo, useState, useEffect, useRef } from 'preact/compat';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useColumnResize, useCopyCell, useCopyRow, useReportExport } from '../hooks';
import { useVirtualScroll } from '../hooks/useVirtualScroll';
import { fetchOfferRows, saveOfferRows, coordinatorFinish } from '../store/offersSlice';
import { selectSelectedOffer } from '../store/selectors';
import ColumnVisibilityModal from './ColumnVisibilityModal';
import ColumnFilter from './ColumnFilter';
import LoadingOverlay from './LoadingOverlay';
import type { OfferRowField } from '../constants/columns';
import { COORDINATOR_FIELDS, NUMBER_FIELDS, FIELD_LABELS, INITIAL_WIDTHS } from '../constants/columns';
import LazySelect from './LazySelect';
import ConfirmModal from './ConfirmModal';
import OfferSkeleton from './OfferSkeleton';
import { copyToClipboard } from '../utils/clipboard';
import GLightbox from 'glightbox';
import 'glightbox/dist/css/glightbox.min.css';

function formatCell(field: string, value: any): string {
    if (value == null) return '';
    const s = String(value);
    if (field === 'num_row' || NUMBER_FIELDS.includes(field as any)) {
        const n = Number(s);
        if (!isNaN(n)) return Number.isInteger(n) ? String(n) : n.toFixed(2);
    }
    return s;
}

const CoordinatorPage = memo(({ onNavigate }: { onNavigate?: (tab: string) => void }) => {
    const dispatch = useAppDispatch();
    const offer = useAppSelector(selectSelectedOffer);
    const rows = useAppSelector(s => s.offers.rows);
    const loadingRows = useAppSelector(s => s.offers.loadingRows);
    const loading = useAppSelector(s => s.offers.loading);
    const refs = useAppSelector(s => s.references.data);
    const selectedCode = offer?.code ?? null;

    const wareCategories = [...(refs?.ware_categories ?? [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
    // Сортировка сезонов по дате окончания (dat_end) убыванием — свежие сверху.
    // ISO-строки сравниваем напрямую (лексикографически = хронологически), пустые уходят вниз.
    const seasons = [...(refs?.seasons ?? [])].sort((a, b) =>
        String(b.dat_end ?? '').localeCompare(String(a.dat_end ?? ''))
    );
    const coordStatuses = refs?.coord_row_statuses ?? [];

    const { handleCellClick, getCellClass, clearSelection: clearCellSelection } = useCopyCell();
    const { handleRowClick, getRowClass, clearSelection: clearRowSelection, selectedRowId } = useCopyRow();
    const tableScrollRef = useRef<HTMLDivElement>(null);

    const [isColumnVisibilityModalOpen, setIsColumnVisibilityModalOpen] = useState(false);
    const [activeCell, setActiveCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
    const [columnFilters, setColumnFilters] = useState<Record<string, Set<any>>>({});
    const [columnRanges, setColumnRanges] = useState<Record<string, { min: string; max: string }>>({});
    const [openFilterField, setOpenFilterField] = useState<OfferRowField | null>(null);
    const [sortConfig, setSortConfig] = useState<{ field: OfferRowField | null; direction: 'asc' | 'desc' }>({ field: 'num_row', direction: 'asc' });
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [editMode, setEditMode] = useState(false);
    const [editedCells, setEditedCells] = useState<Record<string, any>>({});
    const [confirmAction, setConfirmAction] = useState<{ message: string; action: () => void } | null>(null);
    const reportExport = useReportExport(selectedCode ? Number(selectedCode) : null);

    // Quick filters
    const [showUndefined, setShowUndefined] = useState(false);
    const [showNoPhoto, setShowNoPhoto] = useState(false);
    const [searchText, setSearchText] = useState('');

    const storageKey = `visibleColumns_coord_${selectedCode}`;
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
        try { const s = localStorage.getItem(storageKey); if (s) return new Set(JSON.parse(s)); } catch {}
        return new Set(COORDINATOR_FIELDS as string[]);
    });
    const columnOrderKey = `columnOrder_coord_${selectedCode}`;
    const [columnOrder, setColumnOrder] = useState<OfferRowField[]>(() => {
        try {
            const saved = localStorage.getItem(columnOrderKey);
            if (saved) {
                const order = JSON.parse(saved) as OfferRowField[];
                const known = order.filter(f => (COORDINATOR_FIELDS as string[]).includes(f));
                const missing = (COORDINATOR_FIELDS as OfferRowField[]).filter(f => !known.includes(f));
                return [...known, ...missing];
            }
        } catch {}
        return [...COORDINATOR_FIELDS];
    });
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
    const [isResizing, setIsResizing] = useState(false);

    useEffect(() => {
        try { localStorage.setItem(storageKey, JSON.stringify(Array.from(visibleColumns))); } catch {}
    }, [visibleColumns, storageKey]);

    useEffect(() => {
        try { localStorage.setItem(columnOrderKey, JSON.stringify(columnOrder)); } catch {}
    }, [columnOrder, columnOrderKey]);

    // Завершение ресайза по отпусканию мыши — чтобы не путать с drag&drop столбцов
    useEffect(() => {
        const handleMouseUp = () => setIsResizing(false);
        if (isResizing) {
            document.addEventListener('mouseup', handleMouseUp);
            return () => document.removeEventListener('mouseup', handleMouseUp);
        }
    }, [isResizing]);

    // GLightbox for foto_url thumbnails — opened dynamically on click so it works
    // with virtual scroll (static selector binding misses rows rendered after scroll).
    const lightboxRef = useRef<any>(null);
    useEffect(() => () => { if (lightboxRef.current) lightboxRef.current.destroy(); }, []);

    // Со статуса «Отборка» (3) и далее обработка закрыта — принудительно выходим из режима редактирования
    useEffect(() => {
        if (Number(offer?.status_code) >= 3) setEditMode(false);
    }, [offer?.status_code]);

    const filteredColumns = columnOrder.filter(col => visibleColumns.has(col));

    if (!offer) return <OfferSkeleton />;

    // Со статуса «Отборка» (status_code >= 3) обработка закрыта — вкладка только для просмотра
    const editingLocked = Number(offer.status_code) >= 3;

    // "Не определённые" — строка не определена, если ей не назначена товарная группа (ТГ),
    // либо авто-сопоставление выставило неполный/неопределённый статус.
    const isUndefinedRow = (r: any) =>
        !r.ware_group_code || r.status_name === 'Неполное соответствие' || r.status_name === 'Неопределено';

    // Быстрые фильтры (кнопки + поиск)
    const matchesQuick = (r: any) => {
        if (showUndefined && !isUndefinedRow(r)) return false;
        if (showNoPhoto && r.foto_url) return false;
        if (searchText) {
            const s = searchText.toLowerCase();
            const hit =
                String(r.num_row).includes(s) ||
                (r.ware_category_name || '').toLowerCase().includes(s) ||
                (r.foto_url || '').toLowerCase().includes(s) ||
                (r.vendor_ware || '').toLowerCase().includes(s);
            if (!hit) return false;
        }
        return true;
    };

    // Хедер-фильтры по столбцам. excludeField позволяет посчитать опции одного столбца
    // с учётом ВСЕХ остальных активных фильтров (как в Excel) — без невозможных пересечений.
    const matchesColumns = (r: any, excludeField: string | null) =>
        filteredColumns.every(field => {
            if (field === excludeField) return true;
            // Диапазон для числовых столбцов (≥ min, ≤ max)
            const range = columnRanges[field];
            if (range && (range.min !== '' || range.max !== '')) {
                const num = Number(r[field]);
                if (!Number.isFinite(num)) return false;
                if (range.min !== '' && num < Number(range.min)) return false;
                if (range.max !== '' && num > Number(range.max)) return false;
            }
            const filter = columnFilters[field];
            if (!filter || filter.size === 0) return true;
            const value = String(r[field] ?? '').trim();
            return filter.has(value === '' ? '(пусто)' : value);
        });

    // Значения для выпадающего фильтра столбца — из строк, прошедших остальные фильтры
    const filterOptionValues = (field: string) =>
        rows.filter(r => matchesQuick(r) && matchesColumns(r, field)).map((r: any) => r[field]);

    // Итоговый набор строк
    let displayRows = rows.filter(r => matchesQuick(r) && matchesColumns(r, null));

    // Sort
    const sortedRows = displayRows.sort((a, b) => {
        if (!sortConfig.field) return 0;
        const field = sortConfig.field;
        const aVal = a[field], bVal = b[field];
        const isNum = NUMBER_FIELDS.includes(field) || field === 'num_row';
        let cmp = 0;
        if (isNum) {
            cmp = (Number(aVal) || 0) - (Number(bVal) || 0);
        } else {
            cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''), 'ru');
        }
        return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    // Open photo lightbox dynamically — gallery is built from the currently filtered rows
    const openPhoto = (url: string) => {
        const elements: any[] = sortedRows
            .filter(r => r.foto_url)
            .map(r => ({ href: r.foto_url, type: 'image' }));
        const startAt = Math.max(0, elements.findIndex(e => e.href === url));
        if (lightboxRef.current) lightboxRef.current.destroy();
        lightboxRef.current = GLightbox({ elements, loop: true, startAt } as any);
        lightboxRef.current.open();
    };

    const handleFilterChange = (field: string, values: Set<any>) => setColumnFilters(prev => ({ ...prev, [field]: values }));
    const handleRangeChange = (field: string, range: { min: string; max: string }) => setColumnRanges(prev => ({ ...prev, [field]: range }));

    // Перемещение столбцов drag&drop (как на вкладке «Состав»)
    const handleColumnDragStart = (e: DragEvent, column: OfferRowField) => {
        if (isResizing) { e.preventDefault(); return; }
        const target = e.target as HTMLElement;
        if (target.closest('[data-resize-handle]')) { e.preventDefault(); return; }
        setDraggedColumn(column);
        if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', column); }
    };
    const handleColumnDragEnd = () => { setDraggedColumn(null); setDragOverColumn(null); };
    const handleColumnDragOver = (e: DragEvent, column: OfferRowField) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        if (draggedColumn && draggedColumn !== column) setDragOverColumn(column);
    };
    const handleColumnDragLeave = () => setDragOverColumn(null);
    const handleColumnDrop = (e: DragEvent, targetColumn: OfferRowField) => {
        e.preventDefault();
        if (!draggedColumn || draggedColumn === targetColumn) { setDraggedColumn(null); setDragOverColumn(null); return; }
        const newOrder = [...columnOrder];
        const draggedIndex = newOrder.indexOf(draggedColumn as OfferRowField);
        const targetIndex = newOrder.indexOf(targetColumn);
        if (draggedIndex === -1 || targetIndex === -1) { setDraggedColumn(null); setDragOverColumn(null); return; }
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn as OfferRowField);
        setColumnOrder(newOrder);
        setDraggedColumn(null); setDragOverColumn(null);
    };
    const handleSortChange = (field: string, dir: 'asc' | 'desc') => setSortConfig({ field: field as OfferRowField, direction: dir });

    const { headerProps, resizeHandleProps } = useColumnResize(INITIAL_WIDTHS, `columnWidths_coord_${selectedCode}`);

    const toggleRowSelection = (code: number) => {
        const newSet = new Set(selectedRows);
        if (newSet.has(code)) newSet.delete(code); else newSet.add(code);
        setSelectedRows(newSet);
    };

    const selectAllRows = () => setSelectedRows(new Set(sortedRows.map(r => r.code)));


    const handleSaveEdits = async () => {
        if (!selectedCode || Object.keys(editedCells).length === 0) return;
        console.log('[CoordinatorPage] editedCells:', JSON.stringify(editedCells));
        // Group edits by row code
        const rowEdits: Record<number, Record<string, any>> = {};
        for (const [key, value] of Object.entries(editedCells)) {
            const [rowCodeStr, field] = key.split(':');
            const rowCode = Number(rowCodeStr);
            if (!rowEdits[rowCode]) rowEdits[rowCode] = {};
            rowEdits[rowCode][field] = value;
        }
        // Build rows for saveOfferRows
        const rowsToSave = Object.entries(rowEdits).map(([rowCodeStr, fields]) => {
            const rowCode = Number(rowCodeStr);
            const original = rows.find(r => Number(r.code) === rowCode);
            return {
                code: rowCode,
                offer_code: selectedCode,
                num_row: original?.num_row,
                ...fields,
            };
        });
        await dispatch(saveOfferRows({ code: selectedCode, columns: [], rows: rowsToSave }));
        setEditedCells({});
        dispatch(fetchOfferRows({ code: selectedCode }));
    };

    const [massFieldValue, setMassFieldValue] = useState('');
    const [massFieldName, setMassFieldName] = useState('');
    const [massStatus, setMassStatus] = useState('');
    const [massWareGroup, setMassWareGroup] = useState('');
    const [massSeason, setMassSeason] = useState('');

    const hasMassChanges = massStatus || massWareGroup || massSeason || (massFieldName && massFieldValue);

    const handleMassApply = async () => {
        if (!selectedCode || selectedRows.size === 0 || !hasMassChanges) return;

        // Все поля через offer_row_edit
        const fields: Record<string, any> = {};
        if (massWareGroup) fields.ware_group_code = Number(massWareGroup);
        if (massStatus) fields.status_row = Number(massStatus);
        if (massSeason) fields.season_code = Number(massSeason);
        if (massFieldName && massFieldValue) fields[massFieldName] = massFieldValue;

        if (Object.keys(fields).length > 0) {
            const rowsToUpdate = rows.filter(r => selectedRows.has(r.code)).map(r => ({
                code: r.code, offer_code: r.offer_code, num_row: r.num_row, ...fields,
            }));
            await dispatch(saveOfferRows({ code: selectedCode, columns: [], rows: rowsToUpdate }));
        }

        dispatch(fetchOfferRows({ code: selectedCode }));
        // Выделение строк намеренно сохраняем (коды строк при offer_row_edit не меняются),
        // чтобы можно было применить несколько массовых операций подряд. Сбрасываем только поля ввода.
        setMassStatus(''); setMassWareGroup(''); setMassSeason('');
        setMassFieldValue(''); setMassFieldName('');
    };

    const handleFinish = () => {
        if (!selectedCode) return;
        setConfirmAction({
            message: 'Завершить обработку координатором? Все строки будут помечены как "Обработано".',
            action: async () => {
                try {
                    // 1. Set all rows status to "Обработано" (6) via offer_row_edit
                    const allUpdated = rows.map(r => ({
                        code: r.code, offer_code: r.offer_code, num_row: r.num_row, status_row: 6,
                    }));
                    await dispatch(saveOfferRows({ code: selectedCode, columns: [], rows: allUpdated })).unwrap();
                    // 2. Then call coordinator finish
                    await dispatch(coordinatorFinish(selectedCode)).unwrap();
                } catch {
                    // Ошибка уже показана через ErrorToast (state.error). НЕ переходим на «Отборку».
                    setConfirmAction(null);
                    return;
                }
                dispatch(fetchOfferRows({ code: selectedCode }));
                setConfirmAction(null);
                onNavigate?.('selection');
            },
        });
    };

    const ROW_HEIGHT = 36;
    const { containerRef: virtualContainerRef, startIndex, endIndex, totalHeight, offsetY } = useVirtualScroll({
        totalItems: sortedRows.length,
        itemHeight: ROW_HEIGHT,
        overscan: 15,
    });
    const visibleRows = sortedRows.slice(startIndex, endIndex);

    return (
        <div className="h-full flex flex-col relative">
            <LoadingOverlay visible={loadingRows || loading} text="Загрузка..." />

            {/* Toolbar */}
            <div className="px-6 py-3 border-b border-base-300 bg-base-100 flex items-center gap-3 flex-wrap">
                <input
                    type="text" placeholder="Поиск..." className="input input-bordered input-sm w-48"
                    value={searchText} onInput={e => setSearchText((e.target as HTMLInputElement).value)}
                />
                <label className="cursor-pointer">
                    <input type="checkbox" className="hidden" checked={showUndefined} onChange={e => setShowUndefined((e.target as HTMLInputElement).checked)} />
                    <span className={`btn btn-sm ${showUndefined ? 'btn-accent' : 'btn-ghost border border-base-300'}`}>Не определённые</span>
                </label>
                <label className="cursor-pointer">
                    <input type="checkbox" className="hidden" checked={showNoPhoto} onChange={e => setShowNoPhoto((e.target as HTMLInputElement).checked)} />
                    <span className={`btn btn-sm ${showNoPhoto ? 'btn-accent' : 'btn-ghost border border-base-300'}`}>Без фото</span>
                </label>

                <div className="flex-1" />

                <div className="flex items-center gap-3 flex-shrink-0 ml-auto whitespace-nowrap">
                    <button className="btn btn-sm btn-ghost" onClick={() => setIsColumnVisibilityModalOpen(true)} title="Настройка колонок">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </button>
                    {editingLocked ? (
                        <span className="badge badge-ghost badge-sm" title="Предложение на отборке — обработка недоступна">Только просмотр</span>
                    ) : (
                        <label className="cursor-pointer">
                            <input type="checkbox" className="hidden" checked={editMode} onChange={e => setEditMode((e.target as HTMLInputElement).checked)} />
                            <span className={`btn btn-sm ${editMode ? 'btn-warning' : 'btn-ghost border border-base-300'}`}>Редактирование</span>
                        </label>
                    )}
                    {!editingLocked && editMode && Object.keys(editedCells).length > 0 && (
                        <button className="btn btn-sm btn-success" onClick={handleSaveEdits}>Сохранить ({Object.keys(editedCells).length})</button>
                    )}
                    {reportExport.inProgress && (
                        <span className="text-xs text-base-content/60 tabular-nums" title={reportExport.stage ?? ''}>
                            Выгрузка… {reportExport.elapsed}
                        </span>
                    )}
                    {reportExport.ready ? (
                        <button className="btn btn-sm btn-success" onClick={reportExport.download} title="Файл готов">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Скачать
                        </button>
                    ) : (
                        /* title на обёртке: у disabled-кнопки daisyUI отключает pointer-events, подсказка бы не показалась */
                        <span
                            className="inline-flex"
                            title={!editingLocked
                                ? 'Выгрузка доступна после завершения обработки'
                                : reportExport.inProgress ? `Готовится выгрузка… ${reportExport.stage ?? ''}` : 'Выгрузить в Excel'}
                        >
                            <button
                                className="btn btn-sm btn-square btn-ghost border border-base-300"
                                onClick={reportExport.start}
                                disabled={!editingLocked || reportExport.inProgress}
                            >
                                {reportExport.inProgress ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                )}
                            </button>
                        </span>
                    )}
                    {!editingLocked && (
                        <button className="btn btn-sm btn-accent" onClick={handleFinish}>Завершить обработку</button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto p-4" ref={(el: HTMLDivElement | null) => { (tableScrollRef as any).current = el; (virtualContainerRef as any).current = el; }}>
                    <table className="w-auto min-w-full border-collapse text-sm">
                        <thead>
                            <tr className="bg-base-200 font-normal">
                                <th className="py-2 px-1 border border-base-300 w-8"></th>
                                <th className="py-2 px-1 border border-base-300 w-8">
                                    <div className="flex items-center justify-center">
                                    <input type="checkbox" className="w-4 h-4 rounded accent-info cursor-pointer"
                                        checked={selectedRows.size === sortedRows.length && sortedRows.length > 0}
                                        onChange={e => (e.target as HTMLInputElement).checked ? selectAllRows() : setSelectedRows(new Set())}
                                    />
                                    </div>
                                </th>
                                {filteredColumns.map(field => {
                                    const isDragging = draggedColumn === field;
                                    const isDragOver = dragOverColumn === field;
                                    return (
                                    <th key={field} {...headerProps(field)}
                                        className={`py-2 px-2 border border-base-300 relative cursor-move select-none font-normal ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'border-primary border-2 bg-primary/10' : ''}`}
                                        draggable={!isResizing}
                                        onDragStart={e => handleColumnDragStart(e, field)}
                                        onDragEnd={handleColumnDragEnd}
                                        onDragOver={e => handleColumnDragOver(e, field)}
                                        onDragLeave={handleColumnDragLeave}
                                        onDrop={e => handleColumnDrop(e, field)}
                                    >
                                        <div className="flex items-center gap-1.5 pr-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-base-content/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
                                            </svg>
                                            <span className="text-xs">{FIELD_LABELS[field] || field}</span>
                                            {sortConfig.field === field && <span className="text-xs text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
                                        </div>
                                        <div className="absolute bottom-0 right-0" style={{ marginBottom: '2px', marginRight: '2px' }}>
                                            <ColumnFilter field={field} values={filterOptionValues(field)}
                                                selected={columnFilters[field]}
                                                onFilterChange={handleFilterChange} isOpen={openFilterField === field}
                                                onToggle={() => setOpenFilterField(openFilterField === field ? null : field)}
                                                sortConfig={sortConfig} onSortChange={handleSortChange}
                                                isNumberField={NUMBER_FIELDS.includes(field) || field === 'num_row'}
                                                range={columnRanges[field]} onRangeChange={handleRangeChange}
                                            />
                                        </div>
                                        <div data-resize-handle className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-primary/20 z-10" style={{ marginRight: '-4px' }}
                                            onMouseDown={e => { e.stopPropagation(); setIsResizing(true); resizeHandleProps(field).onMouseDown(e as any); }} />
                                    </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.length === 0 ? (
                                <tr>
                                    <td colSpan={filteredColumns.length + 1} className="text-center py-16 text-base-content/60">Нет строк для отображения</td>
                                </tr>
                            ) : (<>
                            {/* Virtual spacer top */}
                            {offsetY > 0 && <tr style={{ height: `${offsetY}px` }} />}
                            {visibleRows.map((row, i) => {
                                const rowIndex = startIndex + i;
                                const rowId = `row-${row.code}`;
                                const isSelected = selectedRows.has(row.code);
                                const rowData: Record<string, string> = {};
                                filteredColumns.forEach(f => { rowData[f] = String(row[f] ?? ''); });

                                return (
                                    <tr key={row.code} style={{ height: `${ROW_HEIGHT}px` }} className={`hover:bg-base-100 ${getRowClass(rowId)} ${isSelected ? 'bg-primary/5' : ''}`}>
                                        <td className="py-1 px-1 border border-base-300 text-center relative cursor-pointer hover:bg-primary/10 transition-colors"
                                            onClick={() => { clearCellSelection(); setActiveCell(null); handleRowClick(rowId, rowData); }}>
                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                className={`h-3 w-3 inline-block transition-colors ${selectedRowId === rowId ? 'text-primary' : 'text-base-content/40'}`}
                                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                            {selectedRowId === rowId && (
                                                <button className="absolute left-full top-1/2 -translate-y-1/2 ml-1 px-1.5 py-0.5 text-[10px] bg-primary text-primary-content rounded whitespace-nowrap z-20 shadow"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        copyToClipboard(Object.values(rowData).join('\t'));
                                                    }}>
                                                    Копировать
                                                </button>
                                            )}
                                        </td>
                                        <td className="py-1 px-1 border border-base-300 text-center">
                                            <input type="checkbox" className="w-4 h-4 rounded accent-info cursor-pointer" checked={isSelected} onChange={() => toggleRowSelection(row.code)} />
                                        </td>
                                        {filteredColumns.map((field, colIndex) => {
                                            const cellId = `${field}-${row.code}`;
                                            const isActive = activeCell?.rowIndex === rowIndex && activeCell?.colIndex === colIndex;
                                            const cellValue = formatCell(field, row[field]);

                                            const editKey = `${row.code}:${field}`;
                                            const isEdited = editKey in editedCells;

                                            // ── Edit Mode: all fields buffer in editedCells ──
                                            if (!editingLocked && editMode && field !== 'num_row') {
                                                if (field === 'ware_category_name') {
                                                    return (
                                                        <td key={field} className={`py-1 px-1 border border-base-300 ${isEdited ? 'bg-warning/10' : ''}`}>
                                                            <LazySelect className="select select-xs select-bordered w-full bg-transparent"
                                                                options={wareCategories.map(c => ({ value: c.code, label: c.name }))}
                                                                value={editedCells[`${row.code}:ware_group_code`] ?? row.ware_group_code ?? ''}
                                                                onChange={v => setEditedCells(prev => ({ ...prev, [`${row.code}:ware_group_code`]: v }))} />
                                                        </td>
                                                    );
                                                }
                                                if (field === 'season_name') {
                                                    return (
                                                        <td key={field} className={`py-1 px-1 border border-base-300 ${isEdited ? 'bg-warning/10' : ''}`}>
                                                            <LazySelect className="select select-xs select-bordered w-full bg-transparent"
                                                                options={seasons.map(s => ({ value: s.code, label: s.name }))}
                                                                value={editedCells[`${row.code}:season_code`] ?? row.season_code ?? ''}
                                                                onChange={v => setEditedCells(prev => ({ ...prev, [`${row.code}:season_code`]: v }))} />
                                                        </td>
                                                    );
                                                }
                                                if (field === 'status_name') {
                                                    return (
                                                        <td key={field} className={`py-1 px-1 border border-base-300 ${isEdited ? 'bg-warning/10' : ''}`}>
                                                            <LazySelect className="select select-xs select-bordered w-full bg-transparent"
                                                                options={coordStatuses.map(s => ({ value: s.code, label: s.name }))}
                                                                value={editedCells[`${row.code}:status_row`] ?? row.status_row ?? ''}
                                                                onChange={v => setEditedCells(prev => ({ ...prev, [`${row.code}:status_row`]: v }))} />
                                                        </td>
                                                    );
                                                }
                                                const editValue = editedCells[editKey] ?? cellValue;
                                                return (
                                                    <td key={field} className={`py-1 px-1 border border-base-300 ${isEdited ? 'bg-warning/10' : ''}`}
                                                        style={{ minWidth: headerProps(field).style.minWidth }}>
                                                        <input type="text" className="input input-xs input-bordered w-full bg-transparent"
                                                            value={editValue}
                                                            onInput={e => setEditedCells(prev => ({ ...prev, [editKey]: (e.target as HTMLInputElement).value }))} />
                                                    </td>
                                                );
                                            }

                                            // ── Normal Mode: dropdowns save immediately (заблокировано при editingLocked) ──
                                            if (!editingLocked && field === 'ware_category_name') {
                                                return (
                                                    <td key={field} className={`py-1 px-1 border border-base-300 ${isActive ? 'ring-2 ring-primary' : ''}`}>
                                                        <LazySelect className="select select-xs select-bordered w-full bg-transparent"
                                                            options={wareCategories.map(c => ({ value: c.code, label: c.name }))}
                                                            value={row.ware_group_code ?? ''}
                                                            onChange={v => {
                                                                const wgCode = Number(v);
                                                                if (wgCode && selectedCode) {
                                                                    const updates: any = { code: row.code, offer_code: row.offer_code, num_row: row.num_row, ware_group_code: wgCode };
                                                                    if (row.season_code) updates.status_row = 6; // auto "Обработано"
                                                                    dispatch(saveOfferRows({ code: selectedCode, columns: [], rows: [updates] }))
                                                                        .then(() => dispatch(fetchOfferRows({ code: selectedCode })));
                                                                }
                                                            }} />
                                                    </td>
                                                );
                                            }
                                            if (!editingLocked && field === 'season_name') {
                                                return (
                                                    <td key={field} className={`py-1 px-1 border border-base-300 ${isActive ? 'ring-2 ring-primary' : ''}`}>
                                                        <LazySelect className="select select-xs select-bordered w-full bg-transparent"
                                                            options={seasons.map(s => ({ value: s.code, label: s.name }))}
                                                            value={row.season_code ?? ''}
                                                            onChange={v => {
                                                                if (selectedCode) {
                                                                    dispatch(saveOfferRows({ code: selectedCode, columns: [], rows: [{ code: row.code, offer_code: row.offer_code, num_row: row.num_row, season_code: Number(v) || null }] }))
                                                                        .then(() => dispatch(fetchOfferRows({ code: selectedCode })));
                                                                }
                                                            }} />
                                                    </td>
                                                );
                                            }
                                            if (!editingLocked && field === 'status_name') {
                                                return (
                                                    <td key={field} className={`py-1 px-1 border border-base-300 ${isActive ? 'ring-2 ring-primary' : ''}`}>
                                                        <LazySelect className="select select-xs select-bordered w-full bg-transparent"
                                                            options={coordStatuses.map(s => ({ value: s.code, label: s.name }))}
                                                            value={row.status_row ?? ''}
                                                            onChange={v => {
                                                                if (selectedCode) {
                                                                    dispatch(saveOfferRows({ code: selectedCode, columns: [], rows: [{ code: row.code, offer_code: row.offer_code, num_row: row.num_row, status_row: Number(v) || null }] }))
                                                                        .then(() => dispatch(fetchOfferRows({ code: selectedCode })));
                                                                }
                                                            }} />
                                                    </td>
                                                );
                                            }

                                            // ── Read-only cell ──
                                            return (
                                                <td key={field}
                                                    className={`${field === 'foto_url' ? 'py-0' : 'py-2'} px-2 border border-base-300 font-normal whitespace-nowrap overflow-hidden ${getCellClass(cellId)} ${isActive ? 'ring-2 ring-primary' : ''}`}
                                                    style={{ minWidth: headerProps(field).style.minWidth }}
                                                    data-cell-id={cellId}
                                                    onClick={e => { e.stopPropagation(); clearRowSelection(); setActiveCell({ rowIndex, colIndex }); handleCellClick(cellId, cellValue); }}
                                                >
                                                    {field === 'foto_url' && cellValue ? (
                                                        <img src={cellValue} alt="" className="h-8 w-8 object-cover rounded cursor-pointer hover:opacity-80 block" loading="lazy"
                                                            onClick={e => { e.stopPropagation(); openPhoto(cellValue); }} />
                                                    ) : cellValue}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            {/* Virtual spacer bottom */}
                            {(totalHeight - offsetY - visibleRows.length * ROW_HEIGHT) > 0 && (
                                <tr style={{ height: `${totalHeight - offsetY - visibleRows.length * ROW_HEIGHT}px` }} />
                            )}
                            </>)}
                        </tbody>
                    </table>
            </div>

            {/* Bottom bar for mass actions */}
            {selectedRows.size > 0 && (
                <div className="border-t border-base-300 bg-base-100 px-4 py-2 flex items-center gap-2 min-w-0">
                    <span className="text-sm text-base-content/60 whitespace-nowrap">{selectedRows.size} выбрано</span>
                    <div className="w-px h-5 bg-base-300" />
                    <select className="select select-bordered select-sm flex-1" value={massStatus}
                        onChange={e => setMassStatus((e.target as HTMLSelectElement).value)}>
                        <option value="">Статус</option>
                        {coordStatuses.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                    <select className="select select-bordered select-sm flex-1" value={massWareGroup}
                        onChange={e => setMassWareGroup((e.target as HTMLSelectElement).value)}>
                        <option value="">ТГ</option>
                        {wareCategories.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                    <select className="select select-bordered select-sm flex-1" value={massSeason}
                        onChange={e => setMassSeason((e.target as HTMLSelectElement).value)}>
                        <option value="">Сезон</option>
                        {seasons.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                    <div className="w-px h-5 bg-base-300" />
                    <select className="select select-bordered select-sm flex-1" value={massFieldName}
                        onChange={e => setMassFieldName((e.target as HTMLSelectElement).value)}>
                        <option value="">Поле</option>
                        {COORDINATOR_FIELDS.filter(f => f !== 'num_row' && f !== 'status_name' && f !== 'ware_category_name' && f !== 'season_name')
                            .map(f => <option key={f} value={f}>{FIELD_LABELS[f] || f}</option>)}
                    </select>
                    <input type="text" className="input input-bordered input-sm flex-1" placeholder="Значение"
                        value={massFieldValue} onInput={e => setMassFieldValue((e.target as HTMLInputElement).value)} />
                    <button className="btn btn-sm btn-accent" disabled={!hasMassChanges || editingLocked} onClick={handleMassApply}>OK</button>
                    <button className="p-0 cursor-pointer text-base-content/40 hover:text-base-content" onClick={() => { setSelectedRows(new Set()); setMassStatus(''); setMassWareGroup(''); setMassSeason(''); setMassFieldName(''); setMassFieldValue(''); }} title="Снять выбор">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            <ConfirmModal
                open={!!confirmAction}
                message={confirmAction?.message || ''}
                onConfirm={() => confirmAction?.action()}
                onCancel={() => setConfirmAction(null)}
            />

            <ColumnVisibilityModal isOpen={isColumnVisibilityModalOpen} onClose={() => setIsColumnVisibilityModalOpen(false)}
                columns={COORDINATOR_FIELDS as string[]} columnLabels={FIELD_LABELS as Record<string, string>}
                visibleColumns={Array.from(visibleColumns)}
                onToggleColumn={newVis => { setVisibleColumns(newVis); setColumnOrder(columnOrder.filter(c => newVis.has(c))); }}
            />
        </div>
    );
});

export default CoordinatorPage;
